import type { LucideIcon } from 'lucide-react';
import {
  ClipboardCheck,
  FileText,
  Mail,
  MessageSquare,
  Phone,
  ShoppingCart,
  Sparkles,
  Star,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';

import type { TranslationKeys } from '@/lib/i18n/en';

export interface WorkflowStep {
  icon: LucideIcon;
  label: string;
  final?: boolean;
}

export interface HelpdeskLogo {
  name: string;
  src: string;
  href: string;
}

export interface Template {
  name: string;
  /**
   * Optional short name used for the HTML `<title>` element when the full
   * `name` would push the SERP title past ~60 characters. Falls back to `name`.
   */
  shortName?: string;
  slug: string;
  description: string;
  category: string;
  icon: LucideIcon;
  supportedSystems: HelpdeskLogo[];
  workflow: WorkflowStep[];
  detailDescription: {
    paragraph1: string;
    paragraph2: string;
  };
  benefits: string[];
  relatedWorkflows: string[];
  gradientColors: string;
}

/**
 * The reader-facing subset of `Template`, all optional.
 *
 * Arabic records live in `src/lib/templates/ar.ts` and are spread over the
 * English record, so a missing or half-authored record degrades field by field
 * to English rather than rendering blanks. `slug`, `icon`, `supportedSystems`,
 * `relatedWorkflows` and `gradientColors` are deliberately absent — they are
 * routing keys and presentation config, not copy.
 */
export type TemplateAr = Partial<
  Pick<
    Template,
    | 'name'
    | 'shortName'
    | 'description'
    | 'category'
    | 'workflow'
    | 'detailDescription'
    | 'benefits'
  >
>;

export function getTemplateBySlug(
  slug: string,
  translations?: TranslationKeys['templates'],
): Template | undefined {
  return getAllTemplates(translations).find((t) => t.slug === slug);
}

export const HELPDESK_LOGOS: HelpdeskLogo[] = [
  {
    name: 'HubSpot',
    src: '/helpdicks/hubspot.png',
    href: '/helpdesks/hubspot',
  },
  {
    name: 'Intercom',
    src: '/helpdicks/intercom.png',
    href: '/helpdesks/intercom',
  },
  {
    name: 'Zendesk',
    src: '/helpdicks/zendesk.png',
    href: '/helpdesks/zendesk',
  },
  {
    name: 'Salesforce',
    src: '/helpdicks/salesforce.png',
    href: '/helpdesks/salesforce',
  },
  {
    name: 'Freshdesk',
    src: '/helpdicks/freshdesk.png',
    href: '/helpdesks/freshdesk',
  },
  {
    name: 'Twilio Flex',
    src: '/helpdicks/flex.png',
    href: '/helpdesks/twilio-flex',
  },
  { name: 'Front', src: '/helpdicks/front.png', href: '/helpdesks/open' },
];

// Helper function to get text with translation fallback
function tt(
  translations: TranslationKeys['templates'] | undefined,
  key: string,
): string {
  if (!translations) return key;
  return (translations as Record<string, string>)[key] || key;
}

// Generate all templates with optional translations
export function getAllTemplates(
  translations?: TranslationKeys['templates'],
): Template[] {
  const t = (key: string) => tt(translations, key);

  return [
    {
      name: t('Automate support tickets quality assurance with AI'),
      slug: 'automate-support-tickets-quality-assurance',
      description: t(
        'Automatically review every support conversation for quality, compliance, and coaching opportunities. Works across all major ticketing systems.',
      ),
      category: 'Customer Support',
      icon: ClipboardCheck,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Ticket') },
        { icon: ClipboardCheck, label: t('AI Review') },
        { icon: Star, label: t('Score'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow automatically reviews every support conversation for quality, compliance, and coaching opportunities. When a ticket is resolved, the AI analyzes the entire conversation thread against your quality criteria—response time, tone, accuracy, policy adherence, and resolution effectiveness. It generates a detailed quality score and highlights specific areas for improvement or recognition.',
        ),
        paragraph2: t(
          'Quality scores and insights are automatically sent to wherever you need them—Slack notifications for urgent coaching needs, Excel reports for weekly reviews, or your data warehouse for deeper analytics. The agentic workflow is flexible enough to integrate with any system you already use. The AI adapts its evaluation criteria based on your specific quality standards and compliance requirements.',
        ),
      },
      benefits: [
        t('Review 100% of conversations, not just random samples'),
        t('Identify coaching opportunities in real-time'),
        t('Ensure compliance with industry regulations'),
        t('Track quality trends across your entire team'),
        t('Reduce manual QA workload by 90%'),
        t('Catch policy violations before they escalate'),
      ],
      relatedWorkflows: [
        t('Automate ticket sentiment analysis with AI'),
        t('Automate skill-based routing with AI'),
        t('Automate escalating to the right team with AI'),
      ],
      gradientColors: 'from-blue-50 via-white to-purple-50',
    },
    {
      name: t('Automate up-sells from support tickets with AI'),
      slug: 'automate-up-sells-from-support-tickets',
      description: t(
        'Identify upsell opportunities during support conversations and convert them into revenue. Seamlessly integrated with your ticketing system.',
      ),
      category: 'Sales & Outreach',
      icon: TrendingUp,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Support') },
        { icon: Sparkles, label: t('AI Detects') },
        { icon: TrendingUp, label: t('Upsell'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow identifies upsell and cross-sell opportunities during support conversations and converts them into revenue. As your support team resolves customer questions, the AI analyzes conversation context—product usage patterns, feature requests, pain points, and account tier—to detect when a customer would benefit from an upgrade, add-on, or premium feature. It then either surfaces the opportunity to your agent in real-time or automatically presents the offer to the customer based on your preferences.',
        ),
        paragraph2: t(
          'Upsell opportunities are sent to your preferred tools—Slack alerts for your sales team, CRM updates for pipeline tracking, or Excel/CSV exports for weekly revenue reports. The agentic workflow is flexible enough to route opportunities wherever your team needs them. Conversion data and attribution tracking integrate seamlessly with your existing reporting systems.',
        ),
      },
      benefits: [
        t('Turn support conversations into revenue opportunities'),
        t('Identify upgrade potential in real-time'),
        t('No awkward sales pitches—AI knows the right moment'),
        t('Track which support interactions drive revenue'),
        t('Seamless handoff to sales team when needed'),
        t('Increase customer lifetime value without extra effort'),
      ],
      relatedWorkflows: [
        t('Automate cart recovery with AI'),
        t('Automate pre-sales qualification with AI'),
        t('Automate ticket sentiment analysis with AI'),
      ],
      gradientColors: 'from-green-50 via-white to-blue-50',
    },
    {
      name: t('Automate cart recovery with AI'),
      slug: 'automate-cart-recovery',
      description: t(
        'Re-engage customers who abandoned their carts with personalized follow-ups via email, SMS, or voice.',
      ),
      category: 'E-commerce',
      icon: ShoppingCart,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: ShoppingCart, label: t('Abandoned') },
        { icon: Mail, label: t('AI Follow-up') },
        { icon: Zap, label: t('Convert'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow re-engages customers who abandoned their shopping carts with personalized follow-up messages. When a customer leaves items in their cart, the AI automatically triggers a sequence of outreach across email, SMS, or voice channels based on your preferences. The system personalizes each message based on cart contents, customer history, and previous purchase behavior.',
        ),
        paragraph2: t(
          'Recovery campaigns and conversion data are sent to your preferred tools—Slack notifications for real-time alerts, CRM updates for attribution tracking, or Excel/CSV exports for campaign analytics. The agentic workflow adapts timing, messaging, and channel selection to maximize conversion rates based on customer behavior patterns.',
        ),
      },
      benefits: [
        t('Recover abandoned carts automatically'),
        t('Personalized outreach across email, SMS, and voice'),
        t('Optimal timing based on customer behavior'),
        t('A/B test messaging and timing automatically'),
        t('Track cart recovery attribution'),
        t('Increase e-commerce revenue without manual work'),
      ],
      relatedWorkflows: [
        t('Automate up-sells from support tickets with AI'),
        t('Automate pre-sales qualification with AI'),
        t('Automate requesting review only when the customer is happy with AI'),
      ],
      gradientColors: 'from-purple-50 via-white to-pink-50',
    },
    {
      name: t('Automate Zendesk custom fields fill up with AI'),
      slug: 'automate-zendesk-custom-fields-fill-up',
      description: t(
        'Automatically populate Zendesk custom fields during conversations, eliminating manual data entry for your support team.',
      ),
      category: 'Customer Support',
      icon: FileText,
      supportedSystems: [
        {
          name: 'Zendesk',
          src: '/helpdicks/zendesk.png',
          href: '/helpdesks/zendesk',
        },
      ],
      workflow: [
        { icon: MessageSquare, label: t('Customer') },
        { icon: Sparkles, label: t('AI Extracts') },
        { icon: FileText, label: t('Fields Filled'), final: true },
      ],
      detailDescription: {
        paragraph1:
          "This workflow automatically extracts relevant information from customer conversations and populates Zendesk custom fields in real-time. As your agents chat with customers, the AI identifies key data points—order numbers, product SKUs, issue categories, customer tier, and any custom fields you've configured. It fills them automatically, eliminating manual copy-pasting and data entry errors.",
        paragraph2: t(
          'Field data is logged directly into Zendesk tickets with full audit trails. The system integrates with your existing Zendesk workflows, triggers, and automations. Custom field rules and validation logic are respected automatically. The AI learns your field naming conventions and adapts to your specific Zendesk configuration.',
        ),
      },
      benefits: [
        t('Eliminate manual data entry for agents'),
        t('Ensure consistent field population'),
        t('Reduce ticket handling time'),
        t('Improve reporting accuracy'),
        t('Full audit trail for compliance'),
        t('Works with all Zendesk custom fields'),
      ],
      relatedWorkflows: [
        t('Automate HubSpot custom fields fill up with AI'),
        t('Automate Salesforce custom fields fill up with AI'),
        t('Automate skill-based routing with AI'),
      ],
      gradientColors: 'from-emerald-50 via-white to-green-50',
    },
    {
      name: t('Automate HubSpot custom fields fill up with AI'),
      slug: 'automate-hubspot-custom-fields-fill-up',
      description: t(
        'Automatically populate HubSpot custom fields during conversations, eliminating manual data entry for your support team.',
      ),
      category: 'Customer Support',
      icon: FileText,
      supportedSystems: [
        {
          name: 'HubSpot',
          src: '/helpdicks/hubspot.png',
          href: '/helpdesks/hubspot',
        },
      ],
      workflow: [
        { icon: MessageSquare, label: t('Customer') },
        { icon: Sparkles, label: t('AI Extracts') },
        { icon: FileText, label: t('Fields Filled'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow automatically extracts relevant information from customer conversations and populates HubSpot custom fields in real-time. As your agents chat with customers, the AI identifies key data points and fills custom properties automatically, eliminating manual data entry.',
        ),
        paragraph2: t(
          'Field data syncs directly into HubSpot contacts and tickets with full audit trails. The system integrates with your existing HubSpot workflows and automation. The AI adapts to your specific HubSpot configuration and custom property definitions.',
        ),
      },
      benefits: [
        t('Eliminate manual data entry for agents'),
        t('Ensure consistent field population'),
        t('Reduce ticket handling time'),
        t('Improve CRM data quality'),
        t('Full audit trail for compliance'),
        t('Works with all HubSpot custom properties'),
      ],
      relatedWorkflows: [
        t('Automate Zendesk custom fields fill up with AI'),
        t('Automate Salesforce custom fields fill up with AI'),
        t('Automate skill-based routing with AI'),
      ],
      gradientColors: 'from-orange-50 via-white to-red-50',
    },
    {
      name: t('Automate Salesforce custom fields fill up with AI'),
      slug: 'automate-salesforce-custom-fields-fill-up',
      description: t(
        'Automatically populate Salesforce custom fields during conversations, eliminating manual data entry for your support team.',
      ),
      category: 'Customer Support',
      icon: FileText,
      supportedSystems: [
        {
          name: 'Salesforce',
          src: '/helpdicks/salesforce.png',
          href: '/helpdesks/salesforce',
        },
      ],
      workflow: [
        { icon: MessageSquare, label: t('Customer') },
        { icon: Sparkles, label: t('AI Extracts') },
        { icon: FileText, label: t('Fields Filled'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow automatically extracts relevant information from customer conversations and populates Salesforce custom fields in real-time. The AI identifies key data points and updates Service Cloud fields automatically, eliminating manual data entry.',
        ),
        paragraph2: t(
          'Field data syncs directly into Salesforce cases with full audit trails. The system integrates with your existing Salesforce workflows, triggers, and validation rules. The AI respects your Salesforce field permissions and configuration.',
        ),
      },
      benefits: [
        t('Eliminate manual data entry for agents'),
        t('Ensure consistent field population'),
        t('Reduce case handling time'),
        t('Improve Salesforce data quality'),
        t('Full audit trail for compliance'),
        t('Works with all Salesforce custom fields'),
      ],
      relatedWorkflows: [
        t('Automate Zendesk custom fields fill up with AI'),
        t('Automate HubSpot custom fields fill up with AI'),
        t('Automate skill-based routing with AI'),
      ],
      gradientColors: 'from-blue-50 via-white to-cyan-50',
    },
    {
      name: t('Automate Intercom custom fields fill up with AI'),
      slug: 'automate-intercom-custom-fields-fill-up',
      description: t(
        'Automatically populate Intercom custom fields during conversations, eliminating manual data entry for your support team.',
      ),
      category: 'Customer Support',
      icon: FileText,
      supportedSystems: [
        {
          name: 'Intercom',
          src: '/helpdicks/intercom.png',
          href: '/helpdesks/intercom',
        },
      ],
      workflow: [
        { icon: MessageSquare, label: t('Customer') },
        { icon: Sparkles, label: t('AI Extracts') },
        { icon: FileText, label: t('Fields Filled'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow automatically extracts relevant information from customer conversations and populates Intercom custom attributes in real-time. The AI identifies key data points and updates user and company attributes automatically.',
        ),
        paragraph2: t(
          'Attribute data syncs directly into Intercom with full audit trails. The system integrates with your existing Intercom workflows and bots. The AI adapts to your specific Intercom data model and custom attribute definitions.',
        ),
      },
      benefits: [
        t('Eliminate manual data entry for agents'),
        t('Ensure consistent attribute population'),
        t('Reduce conversation handling time'),
        t('Improve Intercom data quality'),
        t('Full audit trail for compliance'),
        t('Works with all Intercom custom attributes'),
      ],
      relatedWorkflows: [
        t('Automate Zendesk custom fields fill up with AI'),
        t('Automate HubSpot custom fields fill up with AI'),
        t('Automate skill-based routing with AI'),
      ],
      gradientColors: 'from-blue-50 via-white to-indigo-50',
    },
    {
      name: t('Automate Freshdesk custom fields fill up with AI'),
      slug: 'automate-freshdesk-custom-fields-fill-up',
      description: t(
        'Automatically populate Freshdesk custom fields during conversations, eliminating manual data entry for your support team.',
      ),
      category: 'Customer Support',
      icon: FileText,
      supportedSystems: [
        {
          name: 'Freshdesk',
          src: '/helpdicks/freshdesk.png',
          href: '/helpdesks/freshdesk',
        },
      ],
      workflow: [
        { icon: MessageSquare, label: t('Customer') },
        { icon: Sparkles, label: t('AI Extracts') },
        { icon: FileText, label: t('Fields Filled'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow automatically extracts relevant information from customer conversations and populates Freshdesk custom fields in real-time. The AI identifies key data points and fills ticket fields automatically.',
        ),
        paragraph2: t(
          'Field data is logged directly into Freshdesk tickets. The system integrates with your existing Freshdesk automations and workflows. The AI learns your field structure and adapts to your configuration.',
        ),
      },
      benefits: [
        t('Eliminate manual data entry for agents'),
        t('Ensure consistent field population'),
        t('Reduce ticket handling time'),
        t('Improve reporting accuracy'),
        t('Full audit trail for compliance'),
        t('Works with all Freshdesk custom fields'),
      ],
      relatedWorkflows: [
        t('Automate Zendesk custom fields fill up with AI'),
        t('Automate HubSpot custom fields fill up with AI'),
        t('Automate skill-based routing with AI'),
      ],
      gradientColors: 'from-teal-50 via-white to-cyan-50',
    },
    {
      name: t('Automate Twilio Flex custom fields fill up with AI'),
      slug: 'automate-twilio-flex-custom-fields-fill-up',
      description: t(
        'Automatically populate Twilio Flex custom fields during conversations, eliminating manual data entry for your support team.',
      ),
      category: 'Customer Support',
      icon: FileText,
      supportedSystems: [
        {
          name: 'Twilio Flex',
          src: '/helpdicks/flex.png',
          href: '/helpdesks/twilio-flex',
        },
      ],
      workflow: [
        { icon: MessageSquare, label: t('Customer') },
        { icon: Sparkles, label: t('AI Extracts') },
        { icon: FileText, label: t('Fields Filled'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow automatically extracts relevant information from customer conversations and populates Twilio Flex task attributes in real-time. The AI identifies key data points and updates task attributes automatically.',
        ),
        paragraph2: t(
          'Attribute data is logged directly into Flex tasks. The system integrates with your existing Flex workflows and routing logic. The AI adapts to your specific Flex configuration.',
        ),
      },
      benefits: [
        t('Eliminate manual data entry for agents'),
        t('Ensure consistent attribute population'),
        t('Reduce task handling time'),
        t('Improve reporting accuracy'),
        t('Full audit trail for compliance'),
        t('Works with all Flex custom attributes'),
      ],
      relatedWorkflows: [
        t('Automate Zendesk custom fields fill up with AI'),
        t('Automate skill-based routing with AI'),
        t('Automate escalating to the right team with AI'),
      ],
      gradientColors: 'from-red-50 via-white to-rose-50',
    },
    {
      name: t('Automate Open custom fields fill up with AI'),
      slug: 'automate-open-custom-fields-fill-up',
      description: t(
        'Automatically populate Open custom fields during conversations, eliminating manual data entry for your support team.',
      ),
      category: 'Customer Support',
      icon: FileText,
      supportedSystems: [
        { name: 'Front', src: '/helpdicks/front.png', href: '/helpdesks/open' },
      ],
      workflow: [
        { icon: MessageSquare, label: t('Customer') },
        { icon: Sparkles, label: t('AI Extracts') },
        { icon: FileText, label: t('Fields Filled'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow automatically extracts relevant information from customer conversations and populates Open custom fields in real-time. The AI identifies key data points and fills fields automatically.',
        ),
        paragraph2: t(
          'Field data is logged directly into Open tickets. The system integrates natively with all Open features and workflows. The AI adapts to your specific Open configuration.',
        ),
      },
      benefits: [
        t('Eliminate manual data entry for agents'),
        t('Ensure consistent field population'),
        t('Reduce ticket handling time'),
        t('Improve reporting accuracy'),
        t('Full audit trail for compliance'),
        t('Native Open integration'),
      ],
      relatedWorkflows: [
        t('Automate Zendesk custom fields fill up with AI'),
        t('Automate skill-based routing with AI'),
        t('Automate escalating to the right team with AI'),
      ],
      gradientColors: 'from-emerald-50 via-white to-green-50',
    },
    {
      name: t(
        'Automate following up with a customer using voice calls with AI',
      ),
      shortName: t('Automate voice call follow-ups with AI'),
      slug: 'automate-following-up-with-a-customer-using-voice-calls',
      description: t(
        'Trigger personalized voice call follow-ups based on ticket content or customer communication. AI handles the outreach automatically.',
      ),
      category: 'Customer Support',
      icon: Phone,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Ticket') },
        { icon: Sparkles, label: t('AI Triggers') },
        { icon: Phone, label: t('Voice Call'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow automatically triggers voice call follow-ups when tickets require personal attention. The AI analyzes ticket content, customer sentiment, and urgency to determine when a phone call would resolve the issue faster than text-based communication. It schedules calls at optimal times and prepares agents with full context.',
        ),
        paragraph2: t(
          'Call scheduling and outcomes are sent to your preferred tools—calendar invites, Slack notifications, or CRM task creation. The agentic workflow integrates with your phone system and automatically logs call summaries back into tickets.',
        ),
      },
      benefits: [
        t('Resolve complex issues faster with voice'),
        t('Optimal call timing based on customer timezone'),
        t('Agents get full context before calling'),
        t('Automatic call logging and summaries'),
        t('Improve customer satisfaction scores'),
        t('No manual scheduling required'),
      ],
      relatedWorkflows: [
        t('Automate phone calling from email with AI'),
        t('Automate escalating to the right team with AI'),
        t('Automate ticket sentiment analysis with AI'),
      ],
      gradientColors: 'from-orange-50 via-white to-yellow-50',
    },
    {
      name: t(
        'Automate Slack alerts based on the customer issue or report with AI',
      ),
      shortName: t('Automate Slack alerts for customer issues with AI'),
      slug: 'automate-slack-alerts-based-on-the-customer-issue-or-report',
      description: t(
        'Send intelligent Slack notifications to the right team or channel when AI detects critical issues, high-value customers, or escalation triggers.',
      ),
      category: 'Customer Support',
      icon: MessageSquare,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Issue') },
        { icon: Sparkles, label: t('AI Detects') },
        { icon: MessageSquare, label: t('Slack Alert'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow sends intelligent Slack notifications when the AI detects situations that need immediate attention. Critical bugs, frustrated VIP customers, compliance violations, or escalation triggers automatically alert the right team or channel. The AI includes relevant context, customer details, and recommended actions in each notification.',
        ),
        paragraph2: t(
          'Slack alerts are routed to specific channels, individuals, or groups based on issue type, severity, and team structure. The system integrates with your Slack workspace and respects your notification preferences. Alert patterns and routing rules adapt based on resolution outcomes.',
        ),
      },
      benefits: [
        t('Catch critical issues immediately'),
        t('Route alerts to the right team'),
        t('Full context in every notification'),
        t('Reduce escalation response time'),
        t('Prevent customer churn proactively'),
        t('Customizable alert rules and triggers'),
      ],
      relatedWorkflows: [
        t('Automate escalating to the right team with AI'),
        t('Automate ticket sentiment analysis with AI'),
        t('Automate ticket prioritization based on its content with AI'),
      ],
      gradientColors: 'from-purple-50 via-white to-blue-50',
    },
    {
      name: t('Automate skill-based routing with AI'),
      slug: 'automate-skill-based-routing',
      description: t(
        'Intelligently route tickets to the right team member based on ticket content, expertise required, and agent availability.',
      ),
      category: 'Customer Support',
      icon: Users,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Ticket') },
        { icon: Sparkles, label: t('AI Analyzes') },
        { icon: Users, label: t('Routes to Expert'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow routes tickets to the most qualified agent based on content analysis and expertise matching. The AI reads each ticket, identifies the required skills, and matches it with agents who have the right expertise, availability, and current workload. It considers technical knowledge, product expertise, language skills, and past performance.',
        ),
        paragraph2: t(
          'Routing decisions and outcomes are logged into your ticketing system and sent to analytics tools for optimization. The system learns from resolution patterns and continuously improves routing accuracy. Agent skills and performance metrics update automatically based on ticket outcomes.',
        ),
      },
      benefits: [
        t('Match tickets with the right expert automatically'),
        t('Reduce resolution time significantly'),
        t('Balance workload across team members'),
        t('Improve first-contact resolution rates'),
        t('Agent skill tracking and development'),
        t('Continuous routing optimization'),
      ],
      relatedWorkflows: [
        t('Automate escalating to the right team with AI'),
        t('Automate ticket prioritization based on its content with AI'),
        t('Automate support tickets quality assurance with AI'),
      ],
      gradientColors: 'from-cyan-50 via-white to-blue-50',
    },
    {
      name: t('Automate ticket prioritization based on its content with AI'),
      shortName: t('Automate ticket prioritization with AI'),
      slug: 'automate-ticket-prioritization-based-on-its-content',
      description: t(
        'Automatically set ticket priority levels by analyzing urgency, customer value, and issue severity from the conversation.',
      ),
      category: 'Customer Support',
      icon: TrendingUp,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Ticket') },
        { icon: Sparkles, label: t('AI Evaluates') },
        { icon: Star, label: t('Priority Set'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow automatically sets ticket priority levels by analyzing conversation content, customer tier, and issue urgency. The AI evaluates sentiment, language intensity, business impact, and SLA requirements to assign accurate priority levels. It ensures critical issues get immediate attention while preventing priority inflation.',
        ),
        paragraph2: t(
          'Priority assignments sync directly into your ticketing system and trigger appropriate routing and SLA rules. Priority patterns and accuracy metrics are sent to your analytics tools for monitoring. The system learns from resolution outcomes to improve priority accuracy over time.',
        ),
      },
      benefits: [
        t('Accurate priority assignment from day one'),
        t('Prevent SLA breaches proactively'),
        t('Ensure VIP customers get immediate attention'),
        t('Reduce manual priority triage'),
        t('Improve team efficiency'),
        t('Continuous learning and optimization'),
      ],
      relatedWorkflows: [
        t('Automate skill-based routing with AI'),
        t('Automate escalating to the right team with AI'),
        t('Automate ticket sentiment analysis with AI'),
      ],
      gradientColors: 'from-amber-50 via-white to-orange-50',
    },
    {
      name: t('Automate ticket sentiment analysis with AI'),
      slug: 'automate-ticket-sentiment-analysis',
      description: t(
        'Detect customer sentiment in real-time, flag frustrated customers, and route urgent cases for immediate human attention.',
      ),
      category: 'Customer Support',
      icon: MessageSquare,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Message') },
        { icon: Sparkles, label: t('AI Analyzes') },
        { icon: Star, label: t('Sentiment Score'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow detects customer sentiment in real-time from every message, email, or conversation. The AI analyzes tone, language patterns, and emotional indicators to identify frustrated, angry, or at-risk customers. Sentiment scores are calculated continuously throughout the conversation, allowing for proactive intervention before issues escalate.',
        ),
        paragraph2: t(
          'Sentiment data is sent to wherever you need it—Slack alerts for negative sentiment spikes, dashboard updates for team monitoring, or data warehouse exports for trend analysis. The system triggers escalation workflows when sentiment crosses critical thresholds. Sentiment patterns inform training and quality improvement initiatives.',
        ),
      },
      benefits: [
        t('Detect frustrated customers immediately'),
        t('Prevent escalations before they happen'),
        t('Prioritize at-risk customer conversations'),
        t('Track sentiment trends across your team'),
        t('Improve customer satisfaction proactively'),
        t('Identify training opportunities from sentiment data'),
      ],
      relatedWorkflows: [
        t('Automate escalating to the right team with AI'),
        t(
          'Automate Slack alerts based on the customer issue or report with AI',
        ),
        t('Automate support tickets quality assurance with AI'),
      ],
      gradientColors: 'from-pink-50 via-white to-purple-50',
    },
    {
      name: t('Automate replying to Trustpilot reviews with AI'),
      slug: 'automate-replying-to-trustpilot-reviews',
      description: t(
        'Automatically respond to Trustpilot reviews with personalized, on-brand replies. AI crafts appropriate responses based on review sentiment and content.',
      ),
      category: 'Customer Support',
      icon: Star,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: Star, label: t('Review') },
        { icon: Sparkles, label: t('AI Crafts') },
        { icon: MessageSquare, label: t('Reply Sent'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow automatically responds to Trustpilot reviews with personalized, on-brand replies. The AI reads each review, understands the sentiment and specific feedback, and crafts an appropriate response that addresses customer concerns while maintaining your brand voice. It handles both positive reviews (thanking and reinforcing) and negative reviews (acknowledging and offering solutions).',
        ),
        paragraph2: t(
          'Review responses can be sent automatically or queued for approval based on sentiment and content. Response drafts, approval workflows, and published replies are logged to Slack, email, or your review management dashboard. The AI learns your brand voice and response patterns to maintain consistency.',
        ),
      },
      benefits: [
        t('Respond to every review promptly'),
        t('Maintain consistent brand voice'),
        t('Turn negative reviews into resolution opportunities'),
        t('Save hours of manual review management'),
        t('Improve public review ratings'),
        "Show customers you're listening",
      ],
      relatedWorkflows: [
        t('Automate requesting review only when the customer is happy with AI'),
        t('Automate ticket sentiment analysis with AI'),
        t('Automate support tickets quality assurance with AI'),
      ],
      gradientColors: 'from-yellow-50 via-white to-amber-50',
    },
    {
      name: t(
        'Automate requesting review only when the customer is happy with AI',
      ),
      slug: 'automate-requesting-review-only-when-the-customer-is-happy',
      description: t(
        'Intelligently request reviews only after positive interactions. AI detects customer satisfaction and sends review requests at the perfect moment.',
      ),
      category: 'Customer Support',
      icon: Star,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Happy Customer') },
        { icon: Sparkles, label: t('AI Detects') },
        { icon: Star, label: t('Request Review'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow intelligently requests reviews only after positive customer interactions. The AI analyzes conversation sentiment, resolution quality, and customer satisfaction signals to determine the optimal moment to ask for a review. It never requests reviews after negative experiences, protecting your public ratings and customer relationships.',
        ),
        paragraph2: t(
          'Review requests are sent via email, SMS, or in-app messaging at the perfect timing. Request campaigns and response rates are tracked in your analytics tools. The system learns which customers are most likely to leave reviews and optimizes request timing accordingly.',
        ),
      },
      benefits: [
        t('Only ask happy customers for reviews'),
        t('Improve review ratings significantly'),
        t('Increase review response rates'),
        t('Protect brand reputation'),
        t('Automate entire review collection process'),
        t('Learn optimal timing from response patterns'),
      ],
      relatedWorkflows: [
        t('Automate replying to Trustpilot reviews with AI'),
        t('Automate ticket sentiment analysis with AI'),
        t('Automate support tickets quality assurance with AI'),
      ],
      gradientColors: 'from-green-50 via-white to-emerald-50',
    },
    {
      name: t('Automate escalating to the right team with AI'),
      slug: 'automate-escalating-to-the-right-team',
      description: t(
        'Smart escalation that routes complex issues to specialized teams based on ticket content, urgency, and required expertise.',
      ),
      category: 'Customer Support',
      icon: TrendingUp,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Complex Issue') },
        { icon: Sparkles, label: t('AI Analyzes') },
        { icon: Users, label: t('Escalates'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow identifies when tickets need escalation and routes them to specialized teams automatically. The AI detects complex technical issues, policy exceptions, legal concerns, or high-stakes situations that require expert attention. It analyzes conversation history, customer tier, and issue complexity to trigger smart escalations with full context.',
        ),
        paragraph2: t(
          'Escalation decisions, context, and routing are logged into your ticketing system and sent to team leads via Slack or email. The system tracks escalation patterns and resolution outcomes to optimize future routing decisions. Escalation metrics feed into performance dashboards and capacity planning tools.',
        ),
      },
      benefits: [
        t('Route complex issues to experts automatically'),
        t('Reduce escalation response time'),
        t('Provide full context to escalation teams'),
        t('Prevent issues from sitting in wrong queues'),
        t('Track escalation patterns and trends'),
        t('Improve specialized team utilization'),
      ],
      relatedWorkflows: [
        t('Automate skill-based routing with AI'),
        t('Automate ticket prioritization based on its content with AI'),
        t(
          'Automate Slack alerts based on the customer issue or report with AI',
        ),
      ],
      gradientColors: 'from-indigo-50 via-white to-purple-50',
    },
    {
      name: t('Automate phone calling from email with AI'),
      slug: 'automate-phone-calling-from-email',
      description: t(
        'When email conversations need urgent attention, AI automatically triggers a follow-up phone call to resolve issues faster.',
      ),
      category: 'Customer Support',
      icon: Phone,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: Mail, label: t('Email Thread') },
        { icon: Sparkles, label: t('AI Triggers') },
        { icon: Phone, label: t('Phone Call'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow detects when email conversations are going in circles or need urgent resolution, and automatically triggers a phone call. The AI analyzes email thread length, response delays, customer frustration signals, and issue complexity to determine when voice communication would resolve the issue faster. It schedules calls and prepares agents with email context.',
        ),
        paragraph2: t(
          'Call triggers and scheduling are managed through your preferred tools—calendar invites, Slack notifications, or phone system integrations. Call outcomes are automatically logged back into email tickets. The system learns which situations benefit most from voice escalation.',
        ),
      },
      benefits: [
        t('Resolve stuck email threads quickly'),
        t('Detect frustration before it escalates'),
        t('Save customers from email ping-pong'),
        t('Agents call with full email context'),
        t('Improve resolution speed dramatically'),
        t('Automatic call outcome logging'),
      ],
      relatedWorkflows: [
        t('Automate following up with a customer using voice calls with AI'),
        t('Automate ticket sentiment analysis with AI'),
        t('Automate escalating to the right team with AI'),
      ],
      gradientColors: 'from-rose-50 via-white to-pink-50',
    },
    {
      name: t('Automate pre-sales qualification with AI'),
      slug: 'automate-pre-sales-qualification',
      description: t(
        'Qualify inbound leads automatically by gathering essential information, assessing fit, and routing hot prospects to your sales team.',
      ),
      category: 'Sales & Outreach',
      icon: ClipboardCheck,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Lead Inquiry') },
        { icon: Sparkles, label: t('AI Qualifies') },
        { icon: Users, label: t('Route to Sales'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow qualifies inbound leads automatically by gathering essential information through natural conversation. The AI asks qualifying questions, assesses company size, budget, timeline, and fit with your product. It scores leads based on your criteria and routes hot prospects directly to sales while filtering out unqualified inquiries.',
        ),
        paragraph2: t(
          'Qualified leads are sent directly to your CRM with complete context—Salesforce, HubSpot, or any system you use. Lead scoring, qualification data, and conversation transcripts are logged for sales team review. The system learns from closed-won deals to improve qualification accuracy.',
        ),
      },
      benefits: [
        t('Qualify leads 24/7 without sales team involvement'),
        t('Gather consistent qualification data'),
        t('Route hot leads to sales immediately'),
        t('Filter out unqualified inquiries'),
        t('Provide sales with full conversation context'),
        t('Improve lead-to-opportunity conversion'),
      ],
      relatedWorkflows: [
        t('Automate up-sells from support tickets with AI'),
        t('Automate cart recovery with AI'),
        t('Automate skill-based routing with AI'),
      ],
      gradientColors: 'from-green-50 via-white to-teal-50',
    },
    {
      name: t('AI phone calls for appointment reminders'),
      slug: 'ai-phone-calls-for-appointment-reminders',
      description: t(
        'Automated AI phone calls to remind customers about upcoming appointments, reducing no-shows and cancellations.',
      ),
      category: 'AI Phone Calling',
      icon: Phone,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Appointment') },
        { icon: Sparkles, label: t('AI Schedules') },
        { icon: Phone, label: t('Reminder Call'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow automatically makes phone calls to remind customers about upcoming appointments. AI calls at the optimal time before appointments, confirms attendance, and offers rescheduling if needed. The conversation is natural and personalized with appointment details, customer name, and location.',
        ),
        paragraph2: t(
          'Call outcomes and confirmations are sent to your calendar system, CRM, or scheduling software. No-show rates typically drop 60-80% with automated reminders. The AI handles confirmations, cancellations, and rescheduling requests without human involvement.',
        ),
      },
      benefits: [
        t('Reduce no-shows by 60-80%'),
        t('Save staff time on manual reminder calls'),
        t('Confirm appointments 24/7 automatically'),
        t('Handle rescheduling requests instantly'),
        t('Improve appointment attendance rates'),
        t('Works across healthcare, salons, services'),
      ],
      relatedWorkflows: [
        t('AI phone calls for payment collection'),
        t('AI phone calls for delivery notifications'),
        t('AI phone calls for customer surveys'),
      ],
      gradientColors: 'from-blue-50 via-white to-cyan-50',
    },
    {
      name: t('AI phone calls for payment collection'),
      slug: 'ai-phone-calls-for-payment-collection',
      description: t(
        'Automated AI phone calls for overdue payment reminders and collection follow-ups with empathy and professionalism.',
      ),
      category: 'AI Phone Calling',
      icon: Phone,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Overdue') },
        { icon: Sparkles, label: t('AI Calls') },
        { icon: Phone, label: t('Payment Resolved'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow makes automated phone calls for overdue payment reminders and collections. The AI calls customers with empathy and professionalism, explains the situation, offers payment options, and can process payments over the phone. It escalates to human agents only when needed.',
        ),
        paragraph2: t(
          'Payment outcomes are logged into your billing system, CRM, or accounting software. Collections are handled with sensitivity while maintaining firmness. The AI adapts its approach based on customer history and payment patterns.',
        ),
      },
      benefits: [
        t('Improve collection rates without adding staff'),
        t('Maintain customer relationships during collections'),
        t('Reduce days sales outstanding (DSO)'),
        t('Process payments 24/7'),
        t('Escalate to humans only when necessary'),
        t('Full compliance with collection regulations'),
      ],
      relatedWorkflows: [
        t('AI phone calls for appointment reminders'),
        t('AI phone calls for renewal reminders'),
        t('Automate following up with a customer using voice calls with AI'),
      ],
      gradientColors: 'from-red-50 via-white to-orange-50',
    },
    {
      name: t('AI phone calls for delivery notifications'),
      slug: 'ai-phone-calls-for-delivery-notifications',
      description: t(
        'Automated AI phone calls to notify customers about delivery status, schedule delivery windows, and confirm receipt.',
      ),
      category: 'AI Phone Calling',
      icon: Phone,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: ShoppingCart, label: t('Order Ready') },
        { icon: Sparkles, label: t('AI Calls') },
        { icon: Phone, label: t('Delivery Confirmed'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow makes automated phone calls for delivery notifications and coordination. The AI calls customers to confirm delivery windows, notify about delays, coordinate specific delivery times, and confirm successful deliveries. It handles address verification and special delivery instructions.',
        ),
        paragraph2: t(
          'Delivery confirmations and scheduling preferences are sent to your logistics system, warehouse management software, or delivery tracking tools. The AI coordinates with your delivery team in real-time.',
        ),
      },
      benefits: [
        t('Reduce failed deliveries significantly'),
        t('Coordinate delivery times automatically'),
        t('Confirm addresses and access instructions'),
        t('Handle delivery delays proactively'),
        t('Improve customer delivery experience'),
        t('Save logistics team coordination time'),
      ],
      relatedWorkflows: [
        t('AI phone calls for appointment reminders'),
        t('Automate cart recovery with AI'),
        t('AI phone calls for order confirmations'),
      ],
      gradientColors: 'from-purple-50 via-white to-pink-50',
    },
    {
      name: t('AI phone calls for customer surveys'),
      slug: 'ai-phone-calls-for-customer-surveys',
      description: t(
        'Automated AI phone calls to conduct customer satisfaction surveys, gather feedback, and collect NPS scores.',
      ),
      category: 'AI Phone Calling',
      icon: Phone,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Survey Trigger') },
        { icon: Sparkles, label: t('AI Calls') },
        { icon: Star, label: t('Feedback Collected'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow conducts customer satisfaction surveys via automated phone calls. The AI calls customers at optimal times, asks survey questions naturally, probes for detailed feedback, and records responses. It adapts questions based on customer responses and escalates negative feedback immediately.',
        ),
        paragraph2: t(
          'Survey responses are sent to your analytics platform, CRM, or data warehouse. NPS scores, satisfaction ratings, and verbatim feedback are logged for analysis. The AI detects urgent issues and creates follow-up tickets automatically.',
        ),
      },
      benefits: [
        t('Increase survey response rates dramatically'),
        t('Gather richer feedback than email surveys'),
        t('Detect and escalate detractors immediately'),
        t('Conduct surveys at scale without staff'),
        t('Adapt questions based on responses'),
        t('Track NPS and CSAT trends automatically'),
      ],
      relatedWorkflows: [
        t('Automate ticket sentiment analysis with AI'),
        t('Automate requesting review only when the customer is happy with AI'),
        t('AI phone calls for win-back campaigns'),
      ],
      gradientColors: 'from-teal-50 via-white to-cyan-50',
    },
    {
      name: t('AI phone calls for renewal reminders'),
      slug: 'ai-phone-calls-for-renewal-reminders',
      description: t(
        'Automated AI phone calls to remind customers about upcoming contract or subscription renewals, reducing churn.',
      ),
      category: 'AI Phone Calling',
      icon: Phone,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Renewal Due') },
        { icon: Sparkles, label: t('AI Calls') },
        { icon: Star, label: t('Renewal Confirmed'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow makes automated phone calls before contract or subscription renewals. The AI reaches out proactively, reminds customers about renewal dates, answers questions about new features or pricing, and processes renewals over the phone. It identifies at-risk customers and escalates to account managers.',
        ),
        paragraph2: t(
          'Renewal confirmations and at-risk signals are sent to your CRM, billing system, or customer success platform. The AI tracks renewal conversations and follows up automatically. Churn risk detection triggers retention workflows.',
        ),
      },
      benefits: [
        t('Reduce passive churn significantly'),
        t('Identify at-risk customers proactively'),
        t('Process renewals without manual intervention'),
        t('Save account management time'),
        t('Improve renewal rates'),
        t('Detect upsell opportunities during renewal calls'),
      ],
      relatedWorkflows: [
        t('AI phone calls for win-back campaigns'),
        t('AI phone calls for payment collection'),
        t('Automate up-sells from support tickets with AI'),
      ],
      gradientColors: 'from-indigo-50 via-white to-blue-50',
    },
    {
      name: t('AI phone calls for win-back campaigns'),
      slug: 'ai-phone-calls-for-win-back-campaigns',
      description: t(
        'Automated AI phone calls to re-engage churned customers with personalized offers and win them back.',
      ),
      category: 'AI Phone Calling',
      icon: Phone,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: Users, label: t('Churned Customer') },
        { icon: Sparkles, label: t('AI Calls') },
        { icon: Star, label: t('Customer Returns'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow makes automated phone calls to win back churned or inactive customers. The AI calls with personalized offers, addresses reasons for leaving, presents new features or pricing, and attempts to re-engage. The conversation is empathetic and focused on understanding what went wrong.',
        ),
        paragraph2: t(
          'Win-back outcomes and customer feedback are sent to your CRM and customer success tools. The AI learns which offers and messaging work best for different customer segments. Successful win-backs trigger onboarding workflows automatically.',
        ),
      },
      benefits: [
        t('Re-engage churned customers at scale'),
        t('Understand why customers left'),
        t('Present personalized win-back offers'),
        t('Improve customer lifetime value'),
        t('Test win-back messaging automatically'),
        t('Route interested customers to sales'),
      ],
      relatedWorkflows: [
        t('AI phone calls for renewal reminders'),
        t('AI phone calls for customer surveys'),
        t('Automate cart recovery with AI'),
      ],
      gradientColors: 'from-rose-50 via-white to-red-50',
    },
    {
      name: t('AI phone calls for order confirmations'),
      slug: 'ai-phone-calls-for-order-confirmations',
      description: t(
        'Automated AI phone calls to confirm large or complex orders, verify details, and answer customer questions.',
      ),
      category: 'AI Phone Calling',
      icon: Phone,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: ShoppingCart, label: t('Order Placed') },
        { icon: Sparkles, label: t('AI Calls') },
        { icon: Phone, label: t('Order Confirmed'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow makes automated phone calls to confirm orders, especially high-value or complex purchases. The AI verifies order details, confirms delivery addresses, explains next steps, and answers questions. It ensures customers feel confident about their purchase.',
        ),
        paragraph2: t(
          'Order confirmations and any changes are logged into your e-commerce system, order management software, or fulfillment tools. The AI detects order issues or concerns and creates support tickets automatically.',
        ),
      },
      benefits: [
        t('Reduce order cancellations and buyer remorse'),
        t('Verify high-value orders for fraud prevention'),
        t('Confirm delivery details accurately'),
        t('Answer order questions immediately'),
        t('Improve customer confidence in purchases'),
        t('Detect and resolve order issues early'),
      ],
      relatedWorkflows: [
        t('AI phone calls for delivery notifications'),
        t('Automate cart recovery with AI'),
        t('AI phone calls for customer surveys'),
      ],
      gradientColors: 'from-emerald-50 via-white to-green-50',
    },
    {
      name: t('AI phone calls for proactive outreach campaigns'),
      slug: 'ai-phone-calls-for-proactive-outreach-campaigns',
      description: t(
        'Bulk automated AI phone calls for announcements, promotions, or service updates at scale.',
      ),
      category: 'AI Phone Calling',
      icon: Phone,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: Users, label: t('Campaign List') },
        { icon: Sparkles, label: t('AI Calls') },
        { icon: Phone, label: t('Outreach Complete'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow makes bulk automated phone calls for proactive customer outreach. The AI calls hundreds or thousands of customers to announce new features, share promotions, notify about service changes, or conduct wellness checks. Each call is personalized with customer-specific context.',
        ),
        paragraph2: t(
          'Campaign results, opt-outs, and customer responses are tracked in your CRM or marketing automation platform. The AI adapts messaging based on real-time response patterns. Interested customers are routed to sales or support automatically.',
        ),
      },
      benefits: [
        t('Reach thousands of customers personally'),
        t('Personalized conversations at scale'),
        t('Track campaign performance in real-time'),
        t('Identify interested customers automatically'),
        t('Save massive amounts of calling time'),
        t('Test messaging variations automatically'),
      ],
      relatedWorkflows: [
        t('AI phone calls for customer surveys'),
        t('AI phone calls for win-back campaigns'),
        t('Automate pre-sales qualification with AI'),
      ],
      gradientColors: 'from-violet-50 via-white to-purple-50',
    },
    {
      name: t('Detect churn risk + proactive retention call'),
      slug: 'detect-churn-risk-proactive-retention-call',
      description: t(
        'AI detects customers at risk of churning and automatically triggers retention calls before they cancel.',
      ),
      category: 'Revenue Intelligence',
      icon: Phone,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: Users, label: t('Churn Signal') },
        { icon: Sparkles, label: t('AI Detects') },
        { icon: Phone, label: t('Retention Call'), final: true },
      ],
      detailDescription: {
        paragraph1:
          "This workflow detects customers at risk of churning and triggers proactive retention calls before they cancel. The AI monitors usage patterns, support ticket sentiment, reduced engagement, competitor mentions, and cancellation signals. When churn risk is detected, it automatically schedules a retention call with personalized offers based on the customer's situation.",
        paragraph2: t(
          'Churn risk scores and retention outcomes are sent to your CRM, customer success platform, or revenue analytics tools. The AI learns which retention offers work best for different customer segments and automatically optimizes retention strategies. Successful saves trigger onboarding workflows to ensure long-term engagement.',
        ),
      },
      benefits: [
        t('Catch churn before it happens'),
        t('Save customers worth 10-100x the intervention cost'),
        t('Personalized retention offers automatically'),
        t('Reduce involuntary churn significantly'),
        t('Understand why customers consider leaving'),
        t('Improve customer lifetime value'),
      ],
      relatedWorkflows: [
        t('AI phone calls for renewal reminders'),
        t('AI phone calls for win-back campaigns'),
        t('Automate ticket sentiment analysis with AI'),
      ],
      gradientColors: 'from-red-50 via-white to-orange-50',
    },
    {
      name: t('Identify high-value lead + priority sales routing'),
      slug: 'identify-high-value-lead-priority-sales-routing',
      description: t(
        'AI identifies enterprise leads and routes them to senior sales reps instantly with full context.',
      ),
      category: 'Revenue Intelligence',
      icon: Users,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Lead Inquiry') },
        { icon: Sparkles, label: t('AI Scores') },
        { icon: Users, label: t('Priority Route'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow identifies high-value leads and routes them to your best sales reps instantly. The AI analyzes company domain, employee count, budget signals, urgency language, and technical sophistication to score lead quality. Enterprise prospects get priority routing to senior reps while small inquiries route to junior team members or automation.',
        ),
        paragraph2: t(
          'Lead scores and routing decisions are logged into your CRM with complete conversation context. Sales reps receive Slack notifications for hot leads with AI-generated briefings. The system learns from closed-won deals to improve lead scoring accuracy continuously.',
        ),
      },
      benefits: [
        t('Never miss a high-value opportunity'),
        t('Route enterprise leads to best closers'),
        t('Full context for sales reps instantly'),
        t('Close deals faster with smart routing'),
        t('Improve lead-to-customer conversion'),
        t('Optimize sales team capacity automatically'),
      ],
      relatedWorkflows: [
        t('Automate pre-sales qualification with AI'),
        t('Detect expansion opportunity + account growth call'),
        t('Automate skill-based routing with AI'),
      ],
      gradientColors: 'from-blue-50 via-white to-indigo-50',
    },
    {
      name: t('Detect expansion opportunity + account growth call'),
      slug: 'detect-expansion-opportunity-account-growth-call',
      description: t(
        'AI detects when customers are ready to expand usage and triggers account growth calls with expansion offers.',
      ),
      category: 'Revenue Intelligence',
      icon: TrendingUp,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: Users, label: t('Usage Growth') },
        { icon: Sparkles, label: t('AI Detects') },
        { icon: Phone, label: t('Expansion Call'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow detects expansion opportunities within existing accounts and triggers growth conversations. The AI monitors usage approaching limits, team growth signals, feature requests for premium capabilities, and positive engagement patterns. When expansion signals are strong, it schedules calls with account managers armed with personalized expansion proposals.',
        ),
        paragraph2: t(
          'Expansion opportunities and call outcomes are logged into your CRM and revenue operations tools. The AI calculates expansion revenue potential and prioritizes opportunities by value. Successful expansions trigger billing updates and onboarding workflows automatically.',
        ),
      },
      benefits: [
        t('Identify expansion opportunities automatically'),
        t('Increase account revenue 2-5x'),
        t('Strike while usage momentum is high'),
        t('Personalized expansion offers per account'),
        t('Reduce account manager workload'),
        t('Track expansion win rates and optimize'),
      ],
      relatedWorkflows: [
        t('Automate up-sells from support tickets with AI'),
        t('Detect churn risk + proactive retention call'),
        t('Identify free trial ending + conversion call'),
      ],
      gradientColors: 'from-green-50 via-white to-emerald-50',
    },
    {
      name: t('Identify payment failure + recovery call'),
      slug: 'identify-payment-failure-recovery-call',
      description: t(
        'AI detects failed payments and triggers empathetic recovery calls to update payment methods and prevent churn.',
      ),
      category: 'Revenue Intelligence',
      icon: Phone,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Payment Fails') },
        { icon: Sparkles, label: t('AI Detects') },
        { icon: Phone, label: t('Recovery Call'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow detects payment failures and triggers recovery calls to prevent involuntary churn. The AI identifies failed payments, expired cards, or billing issues and reaches out empathetically to help customers update their payment methods. It offers multiple payment options and can process payments over the phone securely.',
        ),
        paragraph2: t(
          'Payment recovery outcomes are logged into your billing system, subscription management platform, or revenue analytics tools. The AI optimizes call timing based on recovery success patterns. Recovered payments trigger confirmation emails and service restoration automatically.',
        ),
      },
      benefits: [
        t('Recover 60-80% of failed payments'),
        t('Prevent involuntary churn from billing issues'),
        t('Empathetic outreach maintains relationships'),
        t('Process payment updates immediately'),
        t('Reduce revenue leakage significantly'),
        t('Automatic retry logic and follow-ups'),
      ],
      relatedWorkflows: [
        t('AI phone calls for payment collection'),
        t('Detect churn risk + proactive retention call'),
        t('AI phone calls for renewal reminders'),
      ],
      gradientColors: 'from-amber-50 via-white to-yellow-50',
    },
    {
      name: t('Detect usage milestone + upgrade trigger'),
      slug: 'detect-usage-milestone-upgrade-trigger',
      description: t(
        'AI detects when customers hit usage limits or milestones and triggers upgrade conversations at the perfect moment.',
      ),
      category: 'Revenue Intelligence',
      icon: TrendingUp,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: Users, label: t('Milestone Hit') },
        { icon: Sparkles, label: t('AI Triggers') },
        { icon: Phone, label: t('Upgrade Offer'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow detects when customers hit usage milestones or approach plan limits and triggers upgrade conversations. The AI monitors API usage, seat count, storage consumption, feature adoption, and power user behaviors. When customers demonstrate they need more, it triggers personalized upgrade offers via email, in-app messages, or phone calls.',
        ),
        paragraph2: t(
          'Upgrade opportunities and conversion outcomes are tracked in your product analytics, CRM, or revenue tools. The AI tests different offer timing, messaging, and discount strategies to optimize conversion rates. Successful upgrades trigger billing changes and feature unlocks automatically.',
        ),
      },
      benefits: [
        t('Convert free users to paid at optimal timing'),
        t('Capture expansion revenue automatically'),
        t('Personalized upgrade paths per user'),
        t('Test pricing and messaging variations'),
        t('Reduce upgrade friction'),
        t('Increase average revenue per account'),
      ],
      relatedWorkflows: [
        t('Detect expansion opportunity + account growth call'),
        t('Automate up-sells from support tickets with AI'),
        t('Identify feature gap + product-led upsell'),
      ],
      gradientColors: 'from-teal-50 via-white to-cyan-50',
    },
    {
      name: t('Identify referral opportunity + request referral'),
      slug: 'identify-referral-opportunity-request-referral',
      description: t(
        'AI detects happy customers and automatically requests referrals at the perfect moment with incentive offers.',
      ),
      category: 'Revenue Intelligence',
      icon: Star,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: Star, label: t('Happy Customer') },
        { icon: Sparkles, label: t('AI Detects') },
        { icon: Users, label: t('Referral Request'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow identifies the perfect moment to request customer referrals based on satisfaction signals. The AI detects high NPS scores, positive support interactions, product adoption success, and enthusiastic language. It then requests referrals via email, in-app message, or phone call with personalized incentive offers.',
        ),
        paragraph2: t(
          'Referral requests, responses, and conversion outcomes are tracked in your referral program software or CRM. The AI learns optimal referral timing and incentive structures per customer segment. Successful referrals trigger reward fulfillment and new lead workflows automatically.',
        ),
      },
      benefits: [
        t('Lowest CAC acquisition channel'),
        t('Ask only happy customers for referrals'),
        t('Optimal timing based on satisfaction'),
        t('Automated incentive tracking'),
        t('Increase referral program participation'),
        t('Track referral source attribution'),
      ],
      relatedWorkflows: [
        t('Automate requesting review only when the customer is happy with AI'),
        t('Detect usage milestone + upgrade trigger'),
        t('AI phone calls for customer surveys'),
      ],
      gradientColors: 'from-yellow-50 via-white to-amber-50',
    },
    {
      name: t('Detect competitor evaluation + competitive win campaign'),
      slug: 'detect-competitor-evaluation-competitive-win-campaign',
      description: t(
        'AI detects when prospects are comparing alternatives and triggers competitive sales materials and calls.',
      ),
      category: 'Revenue Intelligence',
      icon: TrendingUp,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Competitor Mention') },
        { icon: Sparkles, label: t('AI Detects') },
        { icon: Phone, label: t('Competitive Pitch'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow detects when prospects are actively evaluating competitors and triggers targeted competitive win campaigns. The AI identifies mentions of competitor names, comparison requests, evaluation timelines, and decision-making signals. It automatically sends competitive comparison guides, case studies, and triggers sales calls with competitive positioning talking points.',
        ),
        paragraph2: t(
          'Competitive evaluation signals and campaign outcomes are logged into your CRM and sales intelligence tools. The AI maintains competitive intelligence based on win/loss data and continuously optimizes competitive messaging. Sales reps receive real-time alerts when prospects evaluate alternatives.',
        ),
      },
      benefits: [
        t('Win competitive deals proactively'),
        t('Strike when prospects are deciding'),
        t('Arm sales with competitive intelligence'),
        t('Prevent losses to competitors'),
        t('Track competitive win rates'),
        t('Optimize competitive positioning automatically'),
      ],
      relatedWorkflows: [
        t('Identify high-value lead + priority sales routing'),
        t('Automate pre-sales qualification with AI'),
        t('Identify free trial ending + conversion call'),
      ],
      gradientColors: 'from-indigo-50 via-white to-purple-50',
    },
    {
      name: t('Identify free trial ending + conversion call'),
      slug: 'identify-free-trial-ending-conversion-call',
      description: t(
        'AI detects trial users approaching expiration and triggers conversion calls with onboarding help and offers.',
      ),
      category: 'Revenue Intelligence',
      icon: Phone,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: Users, label: t('Trial Ending') },
        { icon: Sparkles, label: t('AI Analyzes') },
        { icon: Phone, label: t('Conversion Call'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow detects free trial users approaching expiration and triggers conversion campaigns. The AI analyzes trial engagement, feature adoption, usage frequency, and value realization signals. High-engagement trials receive personalized conversion calls offering onboarding support, pricing options, and limited-time discounts. Low-engagement trials get educational content and product tours first.',
        ),
        paragraph2: t(
          'Trial conversion data and outcomes are tracked in your product analytics, CRM, or subscription management tools. The AI tests different conversion strategies—call timing, discount offers, onboarding assistance—and optimizes based on conversion rates. Successful conversions trigger billing setup and customer success onboarding workflows.',
        ),
      },
      benefits: [
        t('Increase trial-to-paid conversion 40%+'),
        t('Convert trials at optimal timing'),
        t('Personalized conversion paths per user'),
        t('Reduce trial drop-off rates'),
        t('Provide hands-on onboarding support'),
        t('Track conversion attribution accurately'),
      ],
      relatedWorkflows: [
        t('Detect usage milestone + upgrade trigger'),
        t('Identify high-value lead + priority sales routing'),
        t('Detect expansion opportunity + account growth call'),
      ],
      gradientColors: 'from-cyan-50 via-white to-blue-50',
    },
    {
      name: t('Detect bulk purchase intent + enterprise sales handoff'),
      slug: 'detect-bulk-purchase-intent-enterprise-sales-handoff',
      description: t(
        'AI detects enterprise buying signals and routes to enterprise sales team with custom pricing authority.',
      ),
      category: 'Revenue Intelligence',
      icon: Users,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Enterprise Signal') },
        { icon: Sparkles, label: t('AI Detects') },
        { icon: Users, label: t('Enterprise Route'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow detects enterprise buying signals and routes prospects to enterprise sales teams immediately. The AI identifies requests for bulk pricing, multiple seat purchases, API access, compliance documentation, custom contracts, and SSO requirements. Enterprise prospects are fast-tracked to senior sales reps with authority for custom pricing and terms.',
        ),
        paragraph2: t(
          'Enterprise lead data and conversation context are logged into your CRM with deal size estimates and urgency indicators. Sales reps receive immediate notifications with AI-generated enterprise qualification summaries. The system prioritizes enterprise opportunities over standard inquiries automatically.',
        ),
      },
      benefits: [
        t('Win enterprise contracts faster'),
        t('Route to reps with pricing authority'),
        t('Full context for enterprise conversations'),
        t('Prioritize highest-value opportunities'),
        t('Reduce enterprise sales cycle length'),
        t('Improve enterprise close rates'),
      ],
      relatedWorkflows: [
        t('Identify high-value lead + priority sales routing'),
        t('Automate pre-sales qualification with AI'),
        t('Detect competitor evaluation + competitive win campaign'),
      ],
      gradientColors: 'from-slate-50 via-white to-neutral-50',
    },
    {
      name: t('Identify feature gap + product-led upsell'),
      slug: 'identify-feature-gap-product-led-upsell',
      description: t(
        'AI detects when customers ask for premium features and triggers product-led upsell offers at the moment of need.',
      ),
      category: 'Revenue Intelligence',
      icon: Sparkles,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Feature Request') },
        { icon: Sparkles, label: t('AI Detects Gap') },
        { icon: TrendingUp, label: t('Upsell Offer'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow detects when customers request features available in premium tiers and triggers instant upsell offers. The AI identifies feature gap signals—requests for advanced capabilities, workarounds for limitations, or questions about premium features. It presents upgrade offers at the exact moment customers realize they need more, maximizing conversion.',
        ),
        paragraph2: t(
          'Feature gap data and upsell outcomes are tracked in your product analytics and revenue tools. The AI learns which features drive upgrades most effectively and prioritizes those in product development. Successful upsells trigger feature unlocks and upgrade onboarding workflows automatically.',
        ),
      },
      benefits: [
        t('Convert customers when they need features most'),
        t('Product-led growth on autopilot'),
        t('Identify high-value feature requests'),
        t('Reduce feature request to upgrade time'),
        t('Inform product roadmap with revenue data'),
        t('Increase expansion revenue automatically'),
      ],
      relatedWorkflows: [
        t('Detect usage milestone + upgrade trigger'),
        t('Automate up-sells from support tickets with AI'),
        t('Detect expansion opportunity + account growth call'),
      ],
      gradientColors: 'from-violet-50 via-white to-purple-50',
    },
    {
      name: t('Automate customer email replies with AI'),
      slug: 'automate-customer-email-replies-with-ai',
      description: t(
        'Auto-draft and send accurate, on-brand replies to customer emails by combining your knowledge base with the live conversation.',
      ),
      category: 'Customer Support',
      icon: Mail,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: Mail, label: t('Email In') },
        { icon: Sparkles, label: t('AI Drafts') },
        { icon: Zap, label: t('Send'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          "This workflow auto-replies to inbound customer emails by reading the message, retrieving your knowledge base, and drafting a response that resolves the issue end-to-end. The AI handles the long tail of repetitive support emails — order status, returns, password resets, account questions — while routing anything ambiguous or sensitive to a human with full context attached.",
        ),
        paragraph2: t(
          'You set the tone, signature, and the threshold for auto-send vs draft-for-review. Replies post to your existing inbox (Zendesk, HubSpot, Intercom, Freshdesk, Salesforce, Front, Gmail) the same way a human reply would, so reporting and CSAT surveys keep firing as usual. Confidence scoring and self-evaluation are built in, with reasoning traces available for audit.',
        ),
      },
      benefits: [
        t('Resolve the long tail of repetitive support email automatically'),
        t('Auto-draft or auto-send based on a confidence threshold you set'),
        t('Brand-consistent tone, signature, and language'),
        t('Per-email reasoning trace and confidence score'),
        t('Cuts email-side AHT 60–80% on routine intents'),
        t('Works across Zendesk, HubSpot, Intercom, Freshdesk, Salesforce, Front, Gmail'),
      ],
      relatedWorkflows: [
        t('Automate support tickets quality assurance with AI'),
        t('Automate ticket sentiment analysis with AI'),
        t('Automate skill-based routing with AI'),
      ],
      gradientColors: 'from-blue-50 via-white to-cyan-50',
    },
    {
      name: t('AI chatbot template starter'),
      slug: 'ai-chatbot-template-starter',
      description: t(
        'A starter chatbot template you can deploy on your website, helpdesk, and messaging channels in minutes. Pre-wired for common support intents.',
      ),
      category: 'Customer Support',
      icon: MessageSquare,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Visitor') },
        { icon: Sparkles, label: t('AI Resolves') },
        { icon: Zap, label: t('Handoff'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'A ready-to-deploy chatbot template that handles the most common customer support intents out of the box: order status, returns, account help, FAQs, escalation. Built on the Open agent runtime, so the same brain handles your chatbot, your email replies, and your voice calls — with one knowledge base and one set of integrations.',
        ),
        paragraph2: t(
          "You drop the chat widget on your site, point the agent at your helpdesk and CRM, and start serving traffic the same day. From there you customize the welcome message, intent taxonomy, escalation rules, and brand voice. The chatbot transcript hands off to a human via your existing helpdesk with the conversation already attached — there's no \"please describe your issue\" repeat.",
        ),
      },
      benefits: [
        t('Pre-wired for the top 20 support intents'),
        t('One knowledge base across chat, email, and voice'),
        t('Brand voice, tone, and signature configurable'),
        t('Native handoff to your helpdesk with full transcript'),
        t('Drop-in widget for any website'),
        t('A/B testable welcome and escalation rules'),
      ],
      relatedWorkflows: [
        t('Automate customer email replies with AI'),
        t('Automate skill-based routing with AI'),
        t('Automate escalating to the right team with AI'),
      ],
      gradientColors: 'from-cyan-50 via-white to-blue-50',
    },
    {
      name: t('Automate Zendesk macros with AI'),
      slug: 'automate-zendesk-macros-with-ai',
      description: t(
        "Replace static Zendesk macros with an AI that picks the right macro, fills the variables, and adapts the language to the conversation in real time.",
      ),
      category: 'Customer Support',
      icon: FileText,
      supportedSystems: [
        {
          name: 'Zendesk',
          src: '/helpdicks/zendesk.png',
          href: '/helpdesks/zendesk',
        },
      ],
      workflow: [
        { icon: MessageSquare, label: t('Ticket') },
        { icon: Sparkles, label: t('AI Picks Macro') },
        { icon: FileText, label: t('Sent'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'This workflow turns your existing Zendesk macros into intelligent, context-aware responses. Instead of agents searching through hundreds of macros to find the right one, the AI reads the ticket, picks the best matching macro, fills in the dynamic variables (name, order number, due date, refund amount), adapts the tone to the customer, and either applies it directly or surfaces it for one-click approval.',
        ),
        paragraph2: t(
          "Macro hit-rate, edit distance, and CSAT-after-macro are tracked per macro so you can see which templates are pulling weight and which need rewriting. The AI learns from agent edits over time — when a human consistently rewrites a macro before sending, the AI starts producing the rewritten version automatically. Compatible with all Zendesk macro types and works alongside your existing triggers and automations.",
        ),
      },
      benefits: [
        t('No more agent macro hunting'),
        t('Macros adapt to tone, urgency, and language automatically'),
        t('Per-macro CSAT and hit-rate analytics'),
        t('AI learns from agent edits to improve macros over time'),
        t('One-click apply or fully autonomous'),
        t('Works with all Zendesk macro types'),
      ],
      relatedWorkflows: [
        t('Automate Zendesk custom fields fill up with AI'),
        t('Automate customer email replies with AI'),
        t('Automate skill-based routing with AI'),
      ],
      gradientColors: 'from-emerald-50 via-white to-teal-50',
    },
    {
      name: t('Automate voicemail transcription and callback with AI'),
      slug: 'automate-voicemail-transcription-and-callback-with-ai',
      description: t(
        'Every voicemail transcribed, classified, and either resolved by AI callback or routed to the right human within minutes — not days.',
      ),
      category: 'AI Phone Calling',
      icon: Phone,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: Phone, label: t('Voicemail') },
        { icon: Sparkles, label: t('AI Classifies') },
        { icon: Phone, label: t('AI Callback'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'Voicemails stop being a dead-letter queue. The AI transcribes the message in real time, classifies the intent against your taxonomy, scores urgency, and decides what to do next. Routine intents (status checks, password resets, simple FAQs, scheduling) get an AI callback within 60 seconds. Urgent or sensitive ones page the right human in Slack/SMS with the transcript and intent already attached.',
        ),
        paragraph2: t(
          "Each voicemail becomes a structured record in your CRM or helpdesk — audio file, transcript, detected intent, sentiment, suggested action, and outcome. Analytics show how many voicemails resolved in-AI vs. needed escalation, average time-to-resolution, and which intents your team should automate next. Multilingual: 100+ languages, transcribed and classified per language.",
        ),
      },
      benefits: [
        t('Voicemails resolved within minutes, not days'),
        t('AI callback for routine intents in under 60 seconds'),
        t('Smart urgency-based human escalation'),
        t('Structured records in your CRM or helpdesk'),
        t('100+ languages with per-language classification'),
        t('Per-intent resolution and escalation analytics'),
      ],
      relatedWorkflows: [
        t('Automate following up with a customer using voice calls with AI'),
        t('AI phone calls for appointment reminders'),
        t('Automate phone calling from email with AI'),
      ],
      gradientColors: 'from-rose-50 via-white to-orange-50',
    },
    {
      name: t('Automate language detection on tickets with AI'),
      slug: 'automate-language-detection-on-tickets-with-ai',
      description: t(
        'Auto-detect the language of every inbound message and route, reply, and translate accordingly — no manual triage.',
      ),
      category: 'Customer Support',
      icon: Sparkles,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Message In') },
        { icon: Sparkles, label: t('AI Detects') },
        { icon: Users, label: t('Native Speaker'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'Multilingual support without the routing headache. The AI auto-detects the language of every inbound ticket, chat message, and email, tags the conversation, and routes it to a native-speaking agent or replies directly in the customer\'s language. Works across 100+ languages including dialect variants (Brazilian Portuguese vs European Portuguese, Latin American Spanish vs European Spanish, simplified vs traditional Chinese).',
        ),
        paragraph2: t(
          "When a native speaker isn't available, the AI replies in the customer's language using your knowledge base, while CC'ing your existing team in their language for oversight. Translation quality is measured per language pair and per intent, so you know where the AI is strong and where humans should step in. Per-language CSAT, FRT, and resolution rate are tracked separately.",
        ),
      },
      benefits: [
        t('Auto-detect and tag language on every conversation'),
        t('Route to native speakers when available'),
        t('AI replies natively when no human is available'),
        t('100+ languages with dialect awareness'),
        t('Per-language analytics: CSAT, FRT, resolution rate'),
        t('Translation quality measured per intent'),
      ],
      relatedWorkflows: [
        t('Automate skill-based routing with AI'),
        t('Automate customer email replies with AI'),
        t('Automate ticket sentiment analysis with AI'),
      ],
      gradientColors: 'from-indigo-50 via-white to-blue-50',
    },
    {
      name: t('Automate PII redaction on tickets with AI'),
      slug: 'automate-pii-redaction-on-tickets-with-ai',
      description: t(
        "Auto-detect and redact PII, PHI, and PCI data from tickets and call transcripts before they leave your environment — for compliance and audit.",
      ),
      category: 'Customer Support',
      icon: FileText,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Ticket') },
        { icon: Sparkles, label: t('AI Detects') },
        { icon: FileText, label: t('Redacted'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          'Customer conversations are full of sensitive data — credit card numbers, SSNs, account numbers, health information, addresses. This workflow auto-detects PII, PHI, and PCI fields in tickets and call transcripts and redacts them before the data flows to your reporting, analytics, AI training, or third-party tools. Built-in detectors cover all major regulated data types out of the box, with custom detectors configurable per business.',
        ),
        paragraph2: t(
          "Redaction is reversible inside your tenant for support agents who legitimately need the data, but irreversible everywhere downstream. Compliance reports show what was redacted, where, and why. Works across all major helpdesks (Zendesk, HubSpot, Intercom, Freshdesk, Salesforce) and all voice transcripts (inbound and outbound). HIPAA-, GDPR-, PCI-aligned out of the box; configurable for SOC 2 and SOX programs.",
        ),
      },
      benefits: [
        t('Auto-detect PII, PHI, PCI in real time'),
        t('Redacted data never reaches reporting, analytics, or third parties'),
        t('Reversible inside the tenant for legitimate support needs'),
        t('Custom detectors configurable per business'),
        t('HIPAA-, GDPR-, PCI-aligned by default'),
        t('Compliance audit reports with full redaction lineage'),
      ],
      relatedWorkflows: [
        t('Automate support tickets quality assurance with AI'),
        t('Automate Zendesk custom fields fill up with AI'),
        t('Automate ticket sentiment analysis with AI'),
      ],
      gradientColors: 'from-amber-50 via-white to-rose-50',
    },
    {
      name: t('Automate ticket tagging and labeling with AI'),
      slug: 'automate-ticket-tagging-and-labeling-with-ai',
      description: t(
        'Stop relying on agents to remember the right tags. The AI labels every ticket consistently for routing, reporting, and root-cause analysis.',
      ),
      category: 'Customer Support',
      icon: FileText,
      supportedSystems: HELPDESK_LOGOS,
      workflow: [
        { icon: MessageSquare, label: t('Ticket') },
        { icon: Sparkles, label: t('AI Tags') },
        { icon: FileText, label: t('Labeled'), final: true },
      ],
      detailDescription: {
        paragraph1: t(
          "Manual ticket tagging is unreliable. Some agents tag every ticket; some tag none. Reporting and routing built on top of those tags inherits all that inconsistency. This workflow auto-tags every inbound ticket against your existing tag taxonomy — product area, intent, urgency, customer segment, root cause — with confidence scores per tag.",
        ),
        paragraph2: t(
          "Tags drive your existing helpdesk routing, SLA timers, and reporting the same way they did before. The difference is they're now applied consistently to 100% of tickets, in real time, across all channels. The AI learns your taxonomy from past data and adapts as you add new tags. Per-tag accuracy, hit-rate, and override-rate are tracked so you know when the taxonomy needs cleanup.",
        ),
      },
      benefits: [
        t('100% of tickets tagged consistently'),
        t('Per-tag confidence scores for human review'),
        t('AI learns your taxonomy from past tickets'),
        t('Drives routing, SLAs, and reporting reliably'),
        t('Real-time across all channels'),
        t('Per-tag accuracy and override-rate analytics'),
      ],
      relatedWorkflows: [
        t('Automate skill-based routing with AI'),
        t('Automate ticket prioritization based on its content with AI'),
        t('Automate Zendesk custom fields fill up with AI'),
      ],
      gradientColors: 'from-teal-50 via-white to-emerald-50',
    },
  ];
}

// Legacy export for backward compatibility
export const TEMPLATES: Template[] = getAllTemplates();

export function templateNameToSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function getAllTemplateSlugs(): string[] {
  return TEMPLATES.map((template) => template.slug);
}
